import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Building2, School, Plus, MapPin, Pencil, X } from "lucide-react";

function SchoolEditor({ school, contacts, templates, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name: school.name, address: school.address || "", notes: school.notes || "",
    default_leadership_ids: school.default_leadership_ids || [], eval_template_id: school.eval_template_id || "",
  });
  const save = async () => {
    try { await api.patch(`/schools/${school.id}`, form); onSaved(); toast.success("School updated."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const leadershipCandidates = contacts.filter((c) => c.associations.some((a) => a.school_id === school.id));
  return (
    <div className="rounded-lg border border-terracotta/40 bg-[#FDF3EE] p-4 space-y-3 fade-up" data-testid={`school-editor-${school.id}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="label">School name</label>
          <input className="input" value={form.name} data-testid={`school-name-${school.id}`}
            onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><label className="label">Address / location</label>
          <input className="input" value={form.address} placeholder="Street, city, state" data-testid={`school-address-${school.id}`}
            onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
      </div>
      <div>
        <label className="label">Default leadership recipients for follow-ups</label>
        <div className="flex flex-wrap gap-2" data-testid={`leadership-picker-${school.id}`}>
          {leadershipCandidates.map((c) => {
            const on = form.default_leadership_ids.includes(c.id);
            return (
              <button key={c.id} type="button" data-testid={`leadership-toggle-${school.id}-${c.id}`}
                onClick={() => setForm({ ...form, default_leadership_ids: on ? form.default_leadership_ids.filter((x) => x !== c.id) : [...form.default_leadership_ids, c.id] })}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors ${
                  on ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4] hover:border-navy"}`}>
                {c.display_name}</button>
            );
          })}
          {leadershipCandidates.length === 0 && <span className="text-xs text-[#78716C]">Add contacts for this school first.</span>}
        </div>
        <p className="text-[11px] text-[#78716C] mt-1.5">Teacher follow-ups always go only to teachers the consultant selected for that session — never the whole school.</p>
      </div>
      <div>
        <label className="label">Evaluation form override</label>
        <select className="input max-w-md" value={form.eval_template_id} data-testid={`eval-template-select-${school.id}`}
          onChange={(e) => setForm({ ...form, eval_template_id: e.target.value })}>
          <option value="">Default — CKingEducation Teacher Feedback</option>
          {templates.filter((t) => t.key !== "default").map((t) => <option key={t.id} value={t.id}>{t.name} (v{t.version})</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary !py-1.5" onClick={save} data-testid={`school-save-${school.id}`}>Save</button>
        <button className="btn-ghost !py-1.5" onClick={onCancel} data-testid={`school-cancel-${school.id}`}>Cancel</button>
      </div>
    </div>
  );
}

export default function Organizations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orgs, setOrgs] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgEditName, setOrgEditName] = useState("");
  const [editingSchool, setEditingSchool] = useState(null);
  const [schoolForms, setSchoolForms] = useState({});

  const load = () => api.get("/organizations").then((r) => setOrgs(r.data));
  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/contacts").then((r) => setContacts(r.data));
      api.get("/evaluation-templates").then((r) => setTemplates(r.data));
    }
  }, []);

  const addOrg = async () => {
    try { await api.post("/organizations", { name: orgName }); setOrgName(""); setShowOrgForm(false); load(); toast.success("Organization added."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const renameOrg = async (id) => {
    try { await api.patch(`/organizations/${id}`, { name: orgEditName }); setEditingOrg(null); load(); toast.success("Organization renamed."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addSchool = async (orgId) => {
    const f = schoolForms[orgId] || {};
    if (!f.name?.trim()) return;
    try { await api.post("/schools", { name: f.name, address: f.address || "", organization_id: orgId }); setSchoolForms({ ...schoolForms, [orgId]: {} }); load(); toast.success("School added."); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!orgs) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="organizations-loading">Loading directory…</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="organizations-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Directory</p>
          <h1 className="section-title text-3xl mt-1">Organizations & Schools</h1>
        </div>
        {isAdmin && (
          <button className="btn-outline" onClick={() => setShowOrgForm(!showOrgForm)} data-testid="add-organization-button">
            <Plus size={15} /> Add organization</button>
        )}
      </div>

      {showOrgForm && (
        <div className="card p-5 flex flex-wrap items-end gap-3 fade-up" data-testid="add-organization-form">
          <div className="flex-1 min-w-[220px]">
            <label className="label">District / organization name</label>
            <input className="input" value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="organization-name-input" />
          </div>
          <button className="btn-primary" onClick={addOrg} disabled={!orgName.trim()} data-testid="save-organization-button">Save</button>
        </div>
      )}

      <div className="space-y-6 fade-up-1">
        {orgs.map((o) => (
          <div key={o.id} className="card p-6" data-testid={`org-card-${o.id}`}>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <div className="w-10 h-10 rounded-xl bg-sand flex items-center justify-center text-terracotta"><Building2 size={19} /></div>
              {editingOrg === o.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input className="input max-w-md" value={orgEditName} data-testid={`org-rename-input-${o.id}`}
                    onChange={(e) => setOrgEditName(e.target.value)} />
                  <button className="btn-primary !py-1.5" onClick={() => renameOrg(o.id)} data-testid={`org-rename-save-${o.id}`}>Save</button>
                  <button className="btn-ghost !py-1.5" onClick={() => setEditingOrg(null)}><X size={14} /></button>
                </div>
              ) : (
                <h2 className="font-serif font-bold text-xl text-navy">{o.name}</h2>
              )}
              {o.is_sample && <span className="badge-neutral" data-testid={`org-sample-badge-${o.id}`}>Sample data</span>}
              {isAdmin && editingOrg !== o.id && (
                <button className="btn-ghost !p-1.5" onClick={() => { setEditingOrg(o.id); setOrgEditName(o.name); }} data-testid={`org-edit-${o.id}`}><Pencil size={14} /></button>
              )}
            </div>
            {o.notes && <p className="text-sm text-[#78716C] mb-4 ml-[52px]">{o.notes}</p>}
            <div className="space-y-3 mt-4">
              {o.schools.map((s) => (
                <div key={s.id} data-testid={`school-card-${s.id}`}>
                  <div className="rounded-lg border border-[#E7E1D4] bg-canvas px-4 py-3 flex flex-wrap items-center gap-3">
                    <School size={15} className="text-terracotta shrink-0" />
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-bold text-navy">{s.name}</div>
                      {s.address
                        ? <div className="text-xs text-[#78716C] flex items-center gap-1 mt-0.5"><MapPin size={11} /> {s.address}</div>
                        : <div className="text-xs text-[#A8A29E] italic mt-0.5">No address on file</div>}
                      {(s.default_leadership_ids || []).length > 0 && (
                        <div className="text-[11px] text-[#57534E] mt-1">
                          Leadership recipients: {s.default_leadership_ids.map((id) => contacts.find((c) => c.id === id)?.display_name || "—").join(", ")}</div>
                      )}
                    </div>
                    {isAdmin && (
                      <button className="btn-ghost !py-1" onClick={() => setEditingSchool(editingSchool === s.id ? null : s.id)} data-testid={`school-edit-${s.id}`}>
                        <Pencil size={13} /> Edit</button>
                    )}
                  </div>
                  {editingSchool === s.id && (
                    <div className="mt-2">
                      <SchoolEditor school={s} contacts={contacts} templates={templates}
                        onSaved={() => { setEditingSchool(null); load(); }} onCancel={() => setEditingSchool(null)} />
                    </div>
                  )}
                </div>
              ))}
              {isAdmin && (
                <div className="rounded-lg border border-dashed border-[#E7E1D4] px-3 py-2 grid sm:grid-cols-[1fr_1fr_40px] gap-2 items-center">
                  <input className="input !py-1.5 text-xs" placeholder="New school name"
                    value={(schoolForms[o.id] || {}).name || ""} data-testid={`add-school-input-${o.id}`}
                    onChange={(e) => setSchoolForms({ ...schoolForms, [o.id]: { ...(schoolForms[o.id] || {}), name: e.target.value } })} />
                  <input className="input !py-1.5 text-xs" placeholder="Address (optional)"
                    value={(schoolForms[o.id] || {}).address || ""} data-testid={`add-school-address-${o.id}`}
                    onChange={(e) => setSchoolForms({ ...schoolForms, [o.id]: { ...(schoolForms[o.id] || {}), address: e.target.value } })} />
                  <button className="btn-ghost !px-2" onClick={() => addSchool(o.id)} data-testid={`add-school-button-${o.id}`}>
                    <Plus size={15} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
        {orgs.length === 0 && <p className="text-sm text-[#78716C]" data-testid="organizations-empty">No organizations assigned to you yet.</p>}
      </div>
    </div>
  );
}
